/**
 * Nyx — album content schemas.
 *
 * Every album shipped on the landing flows through these Zod schemas.
 * They run at module-load time inside src/data/albums/index.ts, so a
 * malformed JSON drop fails the build with a precise field path.
 *
 * Shapes are derived from the prototype JSON in `_prototype/uploads/`.
 * Adding new fields: extend the schema, run `pnpm build`, fix any breaks.
 */

import { z } from 'zod';

const HEX = /^#[0-9a-fA-F]{6}$/;

export const PaletteSchema = z.object({
  bg: z.string().regex(HEX, 'palette.bg must be #RRGGBB'),
  fg: z.string().regex(HEX, 'palette.fg must be #RRGGBB'),
  accent: z.string().regex(HEX, 'palette.accent must be #RRGGBB'),
});
export type Palette = z.infer<typeof PaletteSchema>;

export const TypeStyleSchema = z.enum([
  'stencil',
  'carved',
  'bold-serif',
  'soft-serif',
  'typewriter',
  'wide-display',
]);
export type TypeStyle = z.infer<typeof TypeStyleSchema>;

export const TrackSchema = z.object({
  n: z.number().int().positive(),
  title: z.string().min(1),
  ms: z.number().int().nonnegative(),
  hasMeaning: z.boolean().optional(),
  hinge: z.boolean().optional(),
});
export type Track = z.infer<typeof TrackSchema>;

export const AnchorSchema = z.object({
  track: z.string().min(1),
  note: z.string().min(1),
});
export type Anchor = z.infer<typeof AnchorSchema>;

export const TensionSchema = z.object({
  label: z.string().min(1),
  description: z.string().min(1),
  anchors: z.array(AnchorSchema).min(1),
});
export type Tension = z.infer<typeof TensionSchema>;

export const SignatureMomentSchema = z.object({
  track: z.string().min(1),
  anchor: z.string().min(1),
  pull: z.string().min(1),
  why: z.string().min(1),
});
export type SignatureMoment = z.infer<typeof SignatureMomentSchema>;

/**
 * Compact reference to an adjacent album in the artist's catalog.
 * Used by the §04 Posición y duración spine (Antes / Aquí / Después).
 */
export const DiscographyAlbumSchema = z.object({
  title: z.string().min(1),
  year: z.number().int().min(1900).max(2100),
});
export type DiscographyAlbum = z.infer<typeof DiscographyAlbumSchema>;

export const DeepDiveSchema = z.object({
  artist: z.string().min(1),
  title: z.string().min(1),
  year: z.string().min(4),
  position_label: z.string().min(1),
  apparent_form: z.string().min(1),
  actual_behavior: z.string().min(1),
  form_behavior_gap: z.string().min(1),
  /**
   * Discography neighbours. Optional because:
   *   - the latest album in an artist's catalog has no `next_*`;
   *   - a debut album has no `previous_*`.
   * The §04 Posición y duración block (DiscographySpine) reads these and
   * is currently hidden anyway — gating it on presence is cheap.
   */
  previous_context: z.string().min(1).optional(),
  next_context: z.string().min(1).optional(),
  previous_album: DiscographyAlbumSchema.optional(),
  next_album: DiscographyAlbumSchema.optional(),
  position_summary: z.string().min(1),
  arch_type: z.string().min(1),
  arch_summary: z.string().min(1),
  arch_opening: z.string().min(1),
  arch_middle: z.string().min(1),
  arch_closing: z.string().min(1),
  arch_sequencing_note: z.string().min(1),
  /**
   * Core tensions. Some pipeline outputs ship 4 instead of 3 — the
   * carousel handles dynamic counts (counter + dots), so we widen the
   * range rather than forcing an editorial drop.
   */
  tensions: z.array(TensionSchema).min(3).max(4),
  signature_moments: z.array(SignatureMomentSchema).length(2),
  motifs: z.array(z.string().min(1)).min(2).max(4),
  enduring: z.string().min(1),
  tracks: z.array(TrackSchema).min(1),
  songs_with_meaning: z.array(z.string().min(1)).length(3),
});
export type DeepDive = z.infer<typeof DeepDiveSchema>;

export const SymbolSchema = z.object({
  quote: z.string().min(1),
  meaning: z.string().min(1),
});

export const KeyLineSchema = z.object({
  lyric: z.string().min(1),
  explanation: z.string().min(1),
});

export const AlternativeSchema = z.object({
  label: z.string().min(1),
  description: z.string().min(1),
});

export const LiveSchema = z.object({
  mindset: z.string().min(1),
  listen_for: z.string().min(1).optional(),
  line_to_feel: z.string().min(1),
});

export const SongMeaningSchema = z.object({
  n: z.number().int().positive(),
  title: z.string().min(1),
  duration: z.string().regex(/^\d+:[0-5]\d$/, 'duration must be M:SS'),
  summary: z.string().min(1),
  perspective: z.string().min(1),
  voice: z.string().min(1),
  themes: z.array(z.string().min(1)).min(1).max(5),
  tone: z.array(z.string().min(1)).min(1).max(5),
  symbols: z.array(SymbolSchema).min(1),
  key_lines: z.array(KeyLineSchema),
  alternative: AlternativeSchema.optional(),
  live: LiveSchema,
});
export type SongMeaning = z.infer<typeof SongMeaningSchema>;

/** Hero gap-sentence parts — drives the word-stagger reveal. */
export const HeroLineSchema = z.object({
  apparent: z.string().min(1),
  key: z.string().min(1),
  coda: z.string().min(1),
});
export type HeroLine = z.infer<typeof HeroLineSchema>;

/** Deep-dive bisagra gap — drives the section copy. */
export const DeepDiveLineSchema = z.object({
  apparent: z.string().min(1),
  key: z.string().min(1),
});
export type DeepDiveLine = z.infer<typeof DeepDiveLineSchema>;

/** Section title for the deep-dive heading: "X, leído como una <em>Y</em> Z". */
export const SectionTitleSchema = z.object({
  pre: z.string().min(1),
  em: z.string().min(1),
  post: z.string().min(1),
});
export type SectionTitle = z.infer<typeof SectionTitleSchema>;

export const AlbumLibraryEntrySchema = z.object({
  id: z
    .string()
    .min(1)
    .regex(/^[a-z0-9-]+$/, 'id must be lowercase kebab-case'),
  artist: z.string().min(1),
  album: z.string().min(1),
  year: z.number().int().min(1900).max(2100),
  genre: z.string().min(1),
  hook: z.string().min(1),
  palette: PaletteSchema,
  typeStyle: TypeStyleSchema,
  available: z.boolean(),
  hero: HeroLineSchema,
  deepDiveLine: DeepDiveLineSchema,
  sectionTitle: SectionTitleSchema,
  songsTitle: z.string().min(1).default('Tres canciones, leídas con cuidado.'),
  footerCredit: z.string().min(1),
  /**
   * Optional path to real cover artwork served from /public.
   * When present, the Cover component renders this image instead of
   * the generative type-on-color treatment.
   */
  coverImage: z.string().min(1).optional(),
});
export type AlbumLibraryEntry = z.infer<typeof AlbumLibraryEntrySchema>;

/** Available album: must include deep dive + 3 songs. */
export const AvailableAlbumModuleSchema = z
  .object({
    library: AlbumLibraryEntrySchema.extend({ available: z.literal(true) }),
    deepDive: DeepDiveSchema,
    songs: z.array(SongMeaningSchema).length(3),
  })
  .superRefine((mod, ctx) => {
    // Cross-validate: songs[].title must each appear in deepDive.songs_with_meaning.
    const announced = new Set(mod.deepDive.songs_with_meaning);
    for (const [idx, s] of mod.songs.entries()) {
      if (!announced.has(s.title)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['songs', idx, 'title'],
          message: `song "${s.title}" not present in deepDive.songs_with_meaning`,
        });
      }
    }
    // And: deepDive.songs_with_meaning titles must each have a SongMeaning entry.
    const provided = new Set(mod.songs.map((s) => s.title));
    for (const [idx, t] of mod.deepDive.songs_with_meaning.entries()) {
      if (!provided.has(t)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['deepDive', 'songs_with_meaning', idx],
          message: `"${t}" announced but no matching SongMeaning shipped`,
        });
      }
    }
  });
export type AvailableAlbumModule = z.infer<typeof AvailableAlbumModuleSchema>;

/** Coming-soon album: only library metadata; no deep dive or songs needed. */
export const ComingSoonAlbumModuleSchema = z.object({
  library: AlbumLibraryEntrySchema.extend({ available: z.literal(false) }),
});
export type ComingSoonAlbumModule = z.infer<typeof ComingSoonAlbumModuleSchema>;

export const AlbumModuleSchema = z.union([
  AvailableAlbumModuleSchema,
  ComingSoonAlbumModuleSchema,
]);
export type AlbumModule = z.infer<typeof AlbumModuleSchema>;

/**
 * Pretty-print a Zod error for build-time output.
 * Used inside src/data/albums/index.ts when validation fails.
 */
export function formatAlbumError(albumId: string, error: z.ZodError): string {
  const lines = error.issues.map((issue) => {
    const path = issue.path.length ? issue.path.join('.') : '<root>';
    return `    ${path}: ${issue.message}`;
  });
  return `\n  Album "${albumId}" failed schema validation:\n${lines.join('\n')}\n`;
}
